import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { sendMessage, clearConversation } from "../funtionsBD/aiCoachService";
import { submitFixcode } from "../../api/fixcodeApi";
import { isFixcodeCommand, parseFixcode } from "../../utils/fixcodeCommand";
import { useBrand } from "../../context/BrandContext";
import { OPEN_AI_COACH_EVENT } from "../HomeDailyFocus";
import { CLOSE_AI_COACH_EVENT, startPingfloTour } from "../../tour/tourEvents";
import {
  readActiveHomeRole,
  readStoredUser,
  isPlayerAccount,
  canManageChatPlayerInvites,
  canManageChampionshipMatchPresence,
} from "../../utils/homeRole";
import {
  resolveDigipingOpenRole,
  sanitizeProfilingIntroForRole,
  isStaffHomeRole,
  PLAYER_ONLY_PROFILING_INTROS,
} from "../../utils/digipingOpenContext";
import { readPlanningFocus, requestPlanningRefresh } from "../../utils/planningSubgroups";
import { DigipingMark, AgentMark, DigipingSmartRail, getDimensionMeta } from "../../digiping";
import { resolvePlayerAgent, AGENTS, AGENT_IDS } from "../../utils/agentConfig";
import AutoevalGuideCard from "./AutoevalGuideCard";
import {
  useLiveFullscreen,
  requestLiveFullscreen,
} from "../../hooks/useLiveFullscreen";
import {
  fetchSmartProfile,
  saveSmartProfile,
} from "../funtionsBD/digipingSmartService";
import { importLeagueSpielplan } from "../funtionsBD/competitionService";
import { fetchCoachOpenBrief } from "../funtionsBD/digipingChatService";
import CoachVoiceButton from "./CoachVoiceButton";
import DigipingPeopleChat from "./DigipingPeopleChat";
import { checkBrowserCapability, toCoachLanguage } from "../../voice";
import { cancelTourSpeech } from "../../tour/TourTts";
import {
  RichText,
  normalizeDigipingText,
  visiblePlainSlice,
} from "./digipingRichText";
import {
  applyAppLanguage,
  currentAppLanguage,
  preferredAppLanguage,
} from "../../utils/appLanguages";
import { detectInputLanguage } from "../../utils/detectInputLanguage";
import { LanguageShortcutFab } from "../LanguageShortcutFab";
import ScforhWhoCapsule from "../ScforhWhoCapsule";
import DigipingPlayerInviteRoster from "./DigipingPlayerInviteRoster";
import {
  overlayNumberedProfilingChips,
  shouldOverlayProfilingChips,
} from "../../utils/overlayNumberedProfilingChips";
import {
  dismissVirtualKeyboard,
  pinComposerAboveKeyboard,
  useVisualViewportHeight,
} from "../../hooks/useWizardViewportHeight";
import "./DigipingChat.css";

function isTechnicalProfilingIntro(value) {
  return value === "technical" || value === "technicalUpdate";
}

function scrollChatHistoryToEnd(endEl, behavior = "smooth") {
  const history = endEl?.closest?.(".digiping-chat__history") || endEl?.parentElement;
  if (!history) return;
  const top = history.scrollHeight;
  try {
    history.scrollTo({ top, behavior });
  } catch (_) {
    history.scrollTop = top;
  }
}

/**
 * Grow the composer textarea with the text (voice or typed), up to half the
 * viewport, then scroll inside the field so the user can re-read the draft.
 */
function resizeComposerTextarea(el) {
  if (!el || typeof window === "undefined") return;
  const maxPx = Math.max(48, Math.floor(window.innerHeight * 0.5));
  el.style.height = "auto";
  const next = Math.min(Math.max(el.scrollHeight, 0), maxPx);
  el.style.height = `${next}px`;
  el.style.overflowY = el.scrollHeight > maxPx + 1 ? "auto" : "hidden";
  el.classList.toggle("digiping-chat__input--multiline", next > 40);
}

function looksLikeAdminTableCountQuestion(text) {
  const n = String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return /(how many tables|combien de tables|nombre de tables|cuantas mesas|hoeveel tafels|wie viele tische|quantas mesas)/.test(
    n
  );
}

function looksLikeAdminHallAddressQuestion(text) {
  const n = String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return /(adresse (de )?(la )?(salle|hall)|hall address|direccion|optional(e)? address|adres (van )?(de )?zaal|adresse der halle|morada|endereco)/.test(
    n
  );
}

function looksLikeAdminInvitePlayersQuestion(text) {
  const n = String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  const inviteCue =
    /(invite (the )?players|invitar (a )?jugadores|inviter (les )?joueurs|spelers uitnodigen|spieler einladen|convidar jogadores|player invite list|liste d.?invitation|lista de invitacion|invite panel|panel d.?invitation|joueurs invites|invited players|examin(e|er|ions).{0,60}(joueurs|players)|review.{0,40}invited|ouvrir (la )?liste d.?invitation)/.test(
      n
    );
  const sessionFork =
    /(prochaines seances|organiser.{0,40}seances|organize.{0,40}sessions)/.test(n) &&
    /(joueurs|players|invit)/.test(n);
  return inviteCue || sessionFork;
}

function looksLikeAdminTypedQuestion(text) {
  if (looksLikeAdminTableCountQuestion(text) || looksLikeAdminHallAddressQuestion(text)) {
    return false;
  }
  const n = String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return /(hall name|nom de (la )?salle|nombre (de )?(tu |la )?sala|e-?mail|nom du groupe|group name)/.test(
    n
  );
}

function looksLikeAdminConfirmQuestion(text) {
  if (looksLikeAdminTypedQuestion(text)) return false;
  const n = String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (!/\?|\.{2,}|…/.test(String(text || ""))) return false;
  return /(pret|ready|klaar|listo|bereit|d.?accord|confirm|convient|does that|shall we|veux-tu|souhaites?-tu|souhaitez-vous|voulez-vous|would you|do you (want|agree|confirm)|ok (pour|for)|ca te va|cela te va|passer a (la )?(definition|definir)|lettres?\s*smart|smart\s*letters?)/.test(
    n
  );
}

function DigipingTypewriter({
  text,
  onComplete,
  onTick,
  msPerChar = 9,
  charsPerTick = 4,
  className = "digiping-chat__typewriter",
}) {
  const [visibleCount, setVisibleCount] = useState(0);
  const completedRef = useRef(false);
  const full = normalizeDigipingText(text);

  useEffect(() => {
    completedRef.current = false;
    setVisibleCount(0);
    if (!full) {
      completedRef.current = true;
      onComplete?.();
      return undefined;
    }

    let i = 0;
    const step = Math.max(1, charsPerTick);
    const id = setInterval(() => {
      i = Math.min(full.length, i + step);
      setVisibleCount(i);
      onTick?.();
      if (i >= full.length) {
        clearInterval(id);
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
      }
    }, msPerChar);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full]);
  const done = visibleCount >= full.length && full;

  return done ? (
    <RichText text={full} />
  ) : (
    <p className={className}>
      {visiblePlainSlice(full, visibleCount)}
      {visibleCount < full.length ? (
        <span className="typewriter-caret" aria-hidden="true" />
      ) : null}
    </p>
  );
}

function MessageRow({
  message,
  isStreaming,
  onStreamComplete,
  onStreamTick,
  coachName,
  agentId = 'pingflo',
  canEditAnswer,
  editAnswerLabel,
  onEditAnswer,
  hideSmartDimension = false,
  autoevalBusy = false,
  onAutoevalSubmit,
}) {
  const isBot = message.role === "bot";
  const dim = hideSmartDimension ? null : message.dimension;
  const currentAgentId = message.agentId || agentId || 'pingflo';
  const currentCoachName = message.agentName || coachName;

  if (isBot) {
    return (
      <div className="digiping-chat__ai-row">
        <div className="digiping-chat__avatar" title={currentCoachName}>
          <AgentMark agentId={currentAgentId} size={28} />
        </div>
        <div className="digiping-chat__ai-bubble">
          <div className="digiping-chat__coach-label-row">
            <span className="digiping-chat__label">{currentCoachName}</span>
            {dim ? (
              <span className={`dimension-chip dp-em dp-em--${String(dim).toLowerCase()}`}>
                {dim}
                {message.dimensionLabel ? ` · ${message.dimensionLabel}` : ""}
              </span>
            ) : null}
          </div>
          {isStreaming ? (
            <DigipingTypewriter
              text={message.text}
              onTick={onStreamTick}
              onComplete={onStreamComplete}
            />
          ) : (
            <RichText text={message.text} />
          )}
          {message.autoevalGuide ? (
            <AutoevalGuideCard
              guide={message.autoevalGuide}
              disabled={autoevalBusy || isStreaming}
              submitted={Boolean(message.autoevalSubmitted)}
              onSubmit={onAutoevalSubmit}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="digiping-chat__user-row">
      <div className="digiping-chat__user-bubble">
        <div className="digiping-chat__user-answer-row">
          <p className="digiping-chat__msg-text">{message.text}</p>
          {canEditAnswer && message.editable ? (
            <button
              type="button"
              className="digiping-chat__edit-answer-btn"
              title={editAnswerLabel}
              aria-label={editAnswerLabel}
              onClick={() => onEditAnswer?.(message.id)}
            >
              <EditOutlinedIcon fontSize="small" />
              <span>{editAnswerLabel}</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const QUICK_ACTIONS_BY_ROLE = {
  player: [
    { label: "🎯 My goals", text: "Help me improve toward my training goals" },
    { label: "💡 Exercises", text: "Recommend exercises that match my level and objectives" },
    {
      id: "autoeval",
      labelKey: "ae_chat_quick",
      textKey: "ae_chat_quick_prompt",
      label: "📋 Auto-eval",
      text: "Help me auto-evaluate my last session exercises",
    },
    {
      id: "scout",
      labelKey: "schemaPrepCta",
      textKey: "schemaPrepPrompt",
      label: "🏓 Préparer un adversaire",
      text: "Préparer un adversaire: scout hand, style, distance, short/long receive, weak wing, then propose a game plan from my five schémas.",
    },
    { label: "🧠 Mindset", text: "What should I focus on before my next training session?" },
    {
      label: "💪 Health",
      text: "Remind me of the WHO physical-activity guideline for my age and 2–3 health benefits. Club sessions count toward it — never as a guilt metric.",
    },
  ],
  coach: [
    {
      label: "🧩 Analyser SMART",
      text: "Quand j’ouvre une demande joueur, aide-moi à analyser la requête et son impact SMART : 1 chemin principal + 2 options secondaires, très concis. Je décide.",
    },
    {
      label: "🧩 Personalize",
      text: "Propose un exercice personnalisé catalogue pour un joueur (concis).",
    },
    {
      label: "📊 Presence",
      text: "Who trains the most in my groups recently?",
    },
    {
      label: "📋 Session plan",
      text: "Generate a 60-minute training session plan for my group: numbered blocks with exercise name, type, and duration, aligned with group training goals. Do not cite WHO/OMS physical-activity guidelines.",
    },
  ],
  admin: [
    {
      label: "🎯 Club health",
      text: "Help me complete our club Sport Clubs for Health SMART goals (S·M·A·R·T). Start from the health dimensions that are thinner in the club today and turn them into member reminders and club SMART objectives.",
    },
    {
      label: "🧒 Age groups",
      text: "Propose health-enhancing table-tennis sessions for the age groups in our Club for Health seed. Frame each group with the WHO activity guideline and 2–3 health benefits for that age.",
    },
    {
      label: "✉️ Invite",
      text: "Help me invite players to register and complete player profiling.",
      openInviteRoster: true,
    },
    { label: "👥 Groups", text: "List the groups in my club" },
    { label: "📅 Sessions", text: "Show upcoming training sessions" },
    { label: "📊 Presence", text: "How is club training attendance looking?" },
  ],
};
QUICK_ACTIONS_BY_ROLE.committee = QUICK_ACTIONS_BY_ROLE.admin;

/**
 * Digiping chat.
 * - mode="floating" (default): FAB + fullscreen shell on Home and most routes
 * - mode="embedded": panel-only, for Live Training (same Digiping | Players tabs as Home)
 */
export default function AICoachChat({
  mode = "floating",
  open: controlledOpen,
  onRequestClose,
  sessionContext = null,
  hideHeader = false,
  presentPlayers = [],
  sessionCoachUserId = null,
  initialPeoplePeerId = null,
  defaultTab = "digiping",
  /** Embedded bootstrap: { text?, seedKey?, seedParams?, visible?, home_role? } */
  bootstrapSeed = null,
} = {}) {
  const isEmbedded = mode === "embedded";
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const onPlanningPage = String(location.pathname || "")
    .toLowerCase()
    .includes("planning");
  const onLiveTrainingPage = String(location.pathname || "")
    .toLowerCase()
    .includes("live-training");
  /** Planning / Live Training: Pingflo stays on this session's training plan only. */
  const planAssistMode =
    onPlanningPage ||
    onLiveTrainingPage ||
    Boolean(sessionContext?.session_plan);
  const { brand } = useBrand();
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isEmbedded ? Boolean(controlledOpen) : internalOpen;
  const setOpen = useCallback(
    (next) => {
      if (isEmbedded) {
        if (!next) onRequestClose?.();
        return;
      }
      setInternalOpen(next);
    },
    [isEmbedded, onRequestClose]
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [homeRole, setHomeRole] = useState(() => readActiveHomeRole(readStoredUser()));
  // Not useMemo: Terser can reorder /*#__PURE__*/ memos above their dep bindings (TDZ in prod).
  const storedUser = readStoredUser();
  const canStaffChampionship = canManageChampionshipMatchPresence(storedUser, {
    homeRole,
  });
  const headerClubName = (() => {
    const fromUser =
      storedUser?.club_details?.club_name ||
      storedUser?.club_name ||
      storedUser?.tbl_club?.club_name ||
      null;
    if (fromUser && String(fromUser).trim()) return String(fromUser).trim();
    const fromBrand = brand?.club_name || brand?.brand_name || null;
    if (
      fromBrand &&
      String(fromBrand).trim() &&
      !/^smart\s*tt$/i.test(String(fromBrand).trim())
    ) {
      return String(fromBrand).trim();
    }
    return null;
  })();
  const scforhSeed =
    storedUser?.club_details?.scforh_profile ||
    storedUser?.club?.scforh_profile ||
    null;
  const inviteClubId =
    sessionContext?.club_id ||
    storedUser?.club_id ||
    storedUser?.club_details?.club_id ||
    brand?.club_id ||
    null;
  const digipingLang = currentAppLanguage(i18n);
  const lang2 = digipingLang;
  const [pendingSeed, setPendingSeed] = useState(null);
  const [profilingIntro, setProfilingIntro] = useState(null);
  const [profilingMode, setProfilingMode] = useState(null);
  const [voiceInputMode, setVoiceInputMode] = useState(false);
  /** True while mic is live or waiting for final STT — blocks Send on partials. */
  const [voiceSendBlocked, setVoiceSendBlocked] = useState(false);
  const [voiceLanguage, setVoiceLanguage] = useState(null);
  const [smartProfile, setSmartProfile] = useState(null);
  const isPlayer = isPlayerAccount(storedUser, { homeRole });
  const activeAgent = useMemo(() => {
    return resolvePlayerAgent({
      user: storedUser,
      smartProfile,
      requestedAgent: isPlayer ? null : selectedAgentId,
      homeRole,
    });
  }, [storedUser, smartProfile, selectedAgentId, homeRole, isPlayer]);

  const coachName = activeAgent.name;
  const digipingTag = t(activeAgent.badgeKey, activeAgent.defaultBadge);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [mainTab, setMainTab] = useState(defaultTab || "digiping"); // digiping | people

  useEffect(() => {
    if (planAssistMode && mainTab !== "digiping") {
      setMainTab("digiping");
    }
    if (planAssistMode) {
      setSelectedAgentId("pingflo");
    }
  }, [planAssistMode, mainTab]);

  const [coachWelcomeHandled, setCoachWelcomeHandled] = useState(false);
  const [coachOpenBriefDone, setCoachOpenBriefDone] = useState(false);
  const [openPeerOnce, setOpenPeerOnce] = useState(null);
  const [peopleInitialGroupId, setPeopleInitialGroupId] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  /** Voice transcript is shown, but the keyboard stays down until the user taps the field. */
  const [draftLocked, setDraftLocked] = useState(false);
  const [planningFocus, setPlanningFocus] = useState(() => readPlanningFocus());
  const [inviteRosterOpen, setInviteRosterOpen] = useState(false);
  /** During clubSetup / clubScforh, invite UI stays hidden until Digiping reaches the invite step. */
  const [clubSetupInviteUnlocked, setClubSetupInviteUnlocked] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const spielplanFileRef = useRef(null);
  const seedSentRef = useRef(false);
  const bootstrapSeedFpRef = useRef("");
  const hiddenSeedTurnRef = useRef(false);
  const rewindTurnsRef = useRef(null);
  const latestBotMessageRef = useRef(null);
  const handleSendRef = useRef(null);
  const [editingProfilingAnswerId, setEditingProfilingAnswerId] = useState(null);
  const [smartProfileHandoff, setSmartProfileHandoff] = useState(false);

  useEffect(() => {
    if (planAssistMode) setSmartProfileHandoff(false);
  }, [planAssistMode]);
  const conversationIdRef = useRef(null);
  conversationIdRef.current = conversationId;
  const briefFingerprintRef = useRef("");
  const voiceSupported = checkBrowserCapability().ok;
  const dimensionLabels = getDimensionMeta(lang2);

  // Same immersive fullscreen + browser chrome hide as live training (floating only).
  useLiveFullscreen(isEmbedded ? false : Boolean(open));
  useVisualViewportHeight({
    enabled: Boolean(open) && !isEmbedded,
    heightVar: "--dp-vvh",
    topVar: "--dp-vv-top",
    keyboardClass: "digiping-keyboard-open",
    scrollFocusedFields: false,
  });
  const composerPinCleanupRef = useRef(null);
  const skipComposerFocusRef = useRef(false);
  const composerOpenRef = useRef(false);
  composerOpenRef.current = composerOpen;
  /** While true, Web Speech partials may rewrite the composer (Cursor-style live fix-up). */
  const voiceLiveRef = useRef(false);
  /** idle | live | final — final blocks a late listening(true) from erasing the transcript. */
  const voicePhaseRef = useRef("idle");

  const refreshSmart = useCallback(async () => {
    try {
      const isAdmin = homeRole === "admin";
      const isCoach = homeRole === "coach";
      // Active home-role picker wins: a coach who also plays must still get
      // coach_dev while viewing as coach — never their player technical profile.
      let profile = await fetchSmartProfile(null, lang2);
      // One-time migrate from local profiling snapshot when server row is empty
      if (profile && !profile.exists) {
        try {
          const local = JSON.parse(localStorage.getItem("digipingSmart") || "null");
          if (local?.slots) {
            profile = await saveSmartProfile({
              slots: local.slots,
              activities: local.activities || [],
              status: local.status,
              coachAgreement: local.coachAgreement,
              activeDimension: "S",
              phase: "active",
              profileKind: isAdmin ? "club_scforh" : isCoach ? "coach_dev" : "player",
              lang: lang2,
            });
          } else if (isAdmin) {
            profile = await saveSmartProfile({
              profileKind: "club_scforh",
              phase: "active",
              activeDimension: "S",
              lang: lang2,
            });
          } else if (isCoach) {
            profile = await saveSmartProfile({
              profileKind: "coach_dev",
              phase: "active",
              activeDimension: "S",
              lang: lang2,
            });
          }
        } catch (_) {
          /* ignore */
        }
      } else if (isAdmin && profile?.exists && profile.profileKind !== "club_scforh") {
        try {
          profile = await saveSmartProfile({
            profileKind: "club_scforh",
            lang: lang2,
          });
        } catch (_) {
          /* ignore */
        }
      } else if (
        isCoach &&
        profile?.exists &&
        profile.profileKind !== "coach_dev"
      ) {
        try {
          profile = await saveSmartProfile({
            profileKind: "coach_dev",
            lang: lang2,
          });
        } catch (_) {
          /* ignore */
        }
      } else if (
        homeRole === "player" &&
        profile?.exists &&
        (profile.profileKind === "coach_dev" || profile.profileKind === "club_scforh")
      ) {
        try {
          profile = await saveSmartProfile({
            profileKind: "player",
            lang: lang2,
          });
        } catch (_) {
          /* ignore */
        }
      }
      setSmartProfile(profile);
    } catch (err) {
      console.warn("Digiping SMART refresh failed:", err?.message || err);
    }
  }, [homeRole, lang2]);

  useEffect(() => {
    if (!open) return undefined;
    refreshSmart();
    return undefined;
  }, [open, refreshSmart]);

  const openChat = useCallback(() => {
    if (isEmbedded) return;
    const user = readStoredUser();
    const role = readActiveHomeRole(user) || "player";
    const previousRole = homeRole;
    const roleChanged = role !== previousRole;
    const hadPlayerOnlyFlow =
      isTechnicalProfilingIntro(profilingIntro) ||
      PLAYER_ONLY_PROFILING_INTROS.has(profilingIntro) ||
      profilingMode === "technical";

    setHomeRole(role);

    // Staff (admin/coach/…) must never resume a leftover player technical-profile flow.
    if (isStaffHomeRole(role) && (hadPlayerOnlyFlow || roleChanged)) {
      setProfilingIntro(null);
      setProfilingMode(null);
      setPendingSeed(null);
      seedSentRef.current = false;
      hiddenSeedTurnRef.current = false;
      setEditingProfilingAnswerId(null);
      setClubSetupInviteUnlocked(false);
      setInviteRosterOpen(false);
    }

    // Casual FAB open: never resume SMART goals / technical intro cards.
    // Those launch only via profile, explicit Ask Pingflo, or openDigitalTwin.
    if (
      !isStaffHomeRole(role) &&
      (PLAYER_ONLY_PROFILING_INTROS.has(profilingIntro) ||
        profilingMode === "technical")
    ) {
      setProfilingIntro(null);
      setProfilingMode(null);
      setPendingSeed(null);
      seedSentRef.current = false;
      hiddenSeedTurnRef.current = false;
      setEditingProfilingAnswerId(null);
    }

    if (roleChanged || (isStaffHomeRole(role) && hadPlayerOnlyFlow)) {
      setConversationId(null);
      setStreamingMsgId(null);
      setMessages([]);
      setCoachOpenBriefDone(false);
      setCoachWelcomeHandled(false);
      briefFingerprintRef.current = "";
    }

    requestLiveFullscreen();
    setOpen(true);
  }, [
    isEmbedded,
    setOpen,
    homeRole,
    profilingIntro,
    profilingMode,
  ]);

  const closeChat = useCallback(() => {
    cancelTourSpeech();
    setSmartProfileHandoff(false);
    // Drop intro cards on close so the next casual open is a normal chat.
    setProfilingIntro(null);
    setProfilingMode(null);
    setPendingSeed(null);
    seedSentRef.current = false;
    setOpen(false);
    if (!onPlanningPage) navigate("/home");
  }, [navigate, setOpen, onPlanningPage]);

  const confirmSmartProfileHandoff = useCallback(() => {
    setSmartProfileHandoff(false);
    setOpen(false);
    navigate("/profile", { state: { tab: "smart" } });
  }, [navigate, setOpen]);

  const getContext = useCallback(() => {
    const session = localStorage.getItem("rsessn");
    const groupInfo = localStorage.getItem("grpinf");
    const user = localStorage.getItem("user");
    let ts_id = null;
    let club_id = null;
    let club_name = null;
    let group_name = null;
    let gr_id = null;
    let te_id = null;

    try {
      if (session) {
        const parsed = JSON.parse(session);
        ts_id = parsed?.ts_id || null;
        gr_id = parsed?.gr_id || null;
        te_id = parsed?.te_id || null;
      }
    } catch {}

    if (sessionContext?.ts_id) ts_id = sessionContext.ts_id;
    if (sessionContext?.te_id) te_id = sessionContext.te_id;
    if (sessionContext?.gr_id) gr_id = sessionContext.gr_id;
    if (sessionContext?.club_id) club_id = sessionContext.club_id;
    if (sessionContext?.club_name) club_name = sessionContext.club_name;
    if (sessionContext?.group_name) group_name = sessionContext.group_name;

    let planning = null;
    try {
      planning = readPlanningFocus();
    } catch {
      planning = null;
    }
    const sessionPlanAssist = Boolean(sessionContext?.session_plan);
    const onLiveTrainingRoute =
      String(location.pathname || "").toLowerCase().includes("live-training") ||
      sessionContext?.page === "live_training" ||
      sessionContext?.live_training === true;
    const onPlanning =
      !onLiveTrainingRoute &&
      (sessionPlanAssist ||
        Boolean(planning?.page === "planning") ||
        String(location.pathname || "").toLowerCase().includes("planning") ||
        sessionContext?.page === "planning");
    const trainingPlanFocus =
      onPlanning || onLiveTrainingRoute || sessionPlanAssist;
    if (onPlanning && planning) {
      if (planning.ts_id) ts_id = planning.ts_id;
      if (planning.gr_id) gr_id = planning.gr_id;
      if (planning.te_id) te_id = planning.te_id;
    }

    try {
      if (groupInfo) {
        const parsed = JSON.parse(groupInfo);
        const grpGrId = parsed?.groupDetails?.gr_id || null;
        const grpName = parsed?.groupDetails?.gr_name || null;
        if (gr_id && grpGrId === gr_id) {
          group_name = grpName;
        } else if (!gr_id) {
          gr_id = grpGrId;
          group_name = grpName;
        }
      }
    } catch {}

    let displayName = null;
    let userObj = null;
    try {
      if (user) {
        const parsed = JSON.parse(user);
        userObj = parsed;
        if (!club_id) {
          club_id = parsed?.club_id || parsed?.club_details?.club_id || null;
        }
        if (!club_name) {
          club_name = parsed?.club_details?.club_name ?? parsed?.club_name ?? null;
        }
        const fn = parsed?.user_first_name || parsed?.user_firstname || "";
        const ln = parsed?.user_last_name || parsed?.user_lastname || "";
        displayName =
          parsed?.user_alias || [fn, ln].filter(Boolean).join(" ").trim() || null;
      }
    } catch {}

    if (!club_id) {
      club_id = brand?.club_id || null;
    }
    if (!club_name) {
      const fromBrand = brand?.club_name || brand?.brand_name || null;
      if (
        fromBrand &&
        String(fromBrand).trim() &&
        !/^smart\s*tt$/i.test(String(fromBrand).trim())
      ) {
        club_name = String(fromBrand).trim();
      }
    }

    const language = currentAppLanguage(i18n);
    // Always re-read active home role at send time (picker may have changed).
    let role = "player";
    try {
      role = readActiveHomeRole(userObj) || homeRole || "player";
    } catch {
      role = homeRole || "player";
    }
    setHomeRole(role);
    const stationName =
      sessionContext?.station_name ||
      (onPlanning ? planning?.station_name || null : null);
    const exerciseName =
      sessionContext?.exercise_name ||
      (onPlanning ? planning?.exercise_name || null : null);
    const tsgId =
      sessionContext?.tsg_id ||
      (onPlanning ? planning?.tsg_id || null : null);
    const ctx = {
      ts_id,
      te_id,
      club_id,
      club_name,
      group_name,
      gr_id,
      user_display_name: displayName,
      // Keep coach_name only when speaking as coach/admin (legacy MCP context).
      coach_name: role === "coach" || role === "admin" ? displayName : null,
      agent_id: activeAgent.id,
      language,
      home_role: role,
      live_training: Boolean(onLiveTrainingRoute || sessionContext?.live_training),
      session_plan: sessionPlanAssist || onPlanning || onLiveTrainingRoute,
      training_plan_focus: trainingPlanFocus,
      page: onLiveTrainingRoute
        ? "live_training"
        : onPlanning || sessionPlanAssist
          ? "planning"
          : null,
      tsg_id: tsgId,
      station_name: stationName,
      exercise_name: exerciseName,
      player_search_scope: club_id ? "club" : null,
      restrict_players_to_club: Boolean(club_id),
      restrict_players_to_group: Boolean(gr_id),
    };
    if (sessionContext?.session_date) ctx.session_date = sessionContext.session_date;
    if (sessionContext?.session_duration != null) {
      ctx.session_duration = sessionContext.session_duration;
    }
    if (Array.isArray(sessionContext?.present_player_names)) {
      ctx.present_player_names = sessionContext.present_player_names;
    }
    if (Array.isArray(sessionContext?.group_roster_not_present_names)) {
      ctx.group_roster_not_present_names =
        sessionContext.group_roster_not_present_names;
    }
    if (sessionContext?.similar_last_session) {
      ctx.similar_last_session = sessionContext.similar_last_session;
    }
    if (Array.isArray(sessionContext?.planning_exercise_names)) {
      ctx.planning_exercise_names = sessionContext.planning_exercise_names;
    }
    if (Array.isArray(sessionContext?.current_session_blocks)) {
      ctx.current_session_blocks = sessionContext.current_session_blocks;
    }
    if (sessionContext?.session_plan_block_count != null) {
      ctx.session_plan_block_count = sessionContext.session_plan_block_count;
    }
    return ctx;
  }, [i18n, homeRole, sessionContext, activeAgent.id, location.pathname, brand]);

  const welcomeForRole = useCallback(
    (role) => {
      const lng = role === "admin" ? digipingLang : undefined;
      const opts = lng ? { lng } : undefined;
      if (planAssistMode) {
        const hasBlocks =
          Array.isArray(sessionContext?.current_session_blocks) &&
          sessionContext.current_session_blocks.length > 0;
        if (hasBlocks) {
          return (
            t("aiCoachWelcomeSessionPlanRefine", opts) ||
            t(
              "aiCoachWelcomeSessionPlanRefine",
              "I can see this session already has planned blocks. Tell me what to add or adjust — I'll keep what's already there unless you ask to replace it."
            )
          );
        }
        return (
          t(
            "aiCoachWelcomeSessionPlan",
            opts
          ) ||
          t(
            "aiCoachWelcomeSessionPlan",
            "I'm here for this training session's plan — describe what you want, and I'll help build and adjust it."
          )
        );
      }
      if (role === "admin") return t("aiCoachWelcomeAdmin", opts) || t("aiCoachWelcome", opts);
      if (role === "coach") return t("aiCoachWelcomeCoach", opts) || t("aiCoachWelcome", opts);
      const agentKey = `aiCoachWelcome_${activeAgent.id}`;
      const customAgentWelcome = t(agentKey, opts);
      if (customAgentWelcome && customAgentWelcome !== agentKey) {
        return customAgentWelcome;
      }
      return t("aiCoachWelcomePlayer", opts) || t("aiCoachWelcome", opts);
    },
    [t, digipingLang, activeAgent.id, planAssistMode, sessionContext]
  );

  const myUserId = storedUser?.user_id || null;

  const peopleTabLabel =
    canStaffChampionship
      ? t("players") || "Players"
      : t("coach") || "Coach";

  const showPeopleTabs = !planAssistMode;

  const canInvitePlayers = canManageChatPlayerInvites(homeRole);
  const quickActions = useMemo(() => {
    const base =
      QUICK_ACTIONS_BY_ROLE[homeRole || "player"] || QUICK_ACTIONS_BY_ROLE.player;
    if (homeRole === "player" && activeAgent.id !== "pingpro") {
      return base.filter((a) => a.id !== "scout");
    }
    return base;
  }, [homeRole, activeAgent.id]);

  useEffect(() => {
    scrollChatHistoryToEnd(messagesEndRef.current, "smooth");
  }, [messages, streamingMsgId, loading]);

  useEffect(() => {
    if (!open) {
      setComposerOpen(false);
      setDraftLocked(false);
      return undefined;
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!composerOpen || !inputRef.current) {
      skipComposerFocusRef.current = false;
      return undefined;
    }
    if (skipComposerFocusRef.current) {
      const tmr = setTimeout(() => {
        skipComposerFocusRef.current = false;
        const node = inputRef.current;
        if (!node) return;
        node.readOnly = true;
        dismissVirtualKeyboard(node);
        resizeComposerTextarea(node);
        // dismissVirtualKeyboard clears readOnly after 80ms; keep the draft locked.
        window.setTimeout(() => {
          const again = inputRef.current;
          if (again && document.activeElement !== again) again.readOnly = true;
        }, 120);
      }, 50);
      return () => clearTimeout(tmr);
    }
    const tmr = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.readOnly = false;
      el.focus({ preventScroll: true });
      resizeComposerTextarea(el);
    }, 50);
    return () => clearTimeout(tmr);
  }, [composerOpen]);

  useLayoutEffect(() => {
    if (!composerOpen) return;
    resizeComposerTextarea(inputRef.current);
  }, [input, composerOpen]);

  useEffect(() => {
    if (!composerOpen) return undefined;
    const onResize = () => resizeComposerTextarea(inputRef.current);
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [composerOpen]);

  useEffect(() => {
    return () => {
      composerPinCleanupRef.current?.();
      composerPinCleanupRef.current = null;
    };
  }, []);

  useEffect(() => {
    const sync = (event) => {
      setPlanningFocus(event?.detail || readPlanningFocus());
    };
    window.addEventListener("smarttt:planning-focus", sync);
    return () => window.removeEventListener("smarttt:planning-focus", sync);
  }, []);

  useEffect(() => {
    if (!open || isEmbedded) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.classList.add("digiping-chat-open");
    applyAppLanguage(i18n, preferredAppLanguage(readStoredUser(), i18n));
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.classList.remove("digiping-chat-open");
    };
  }, [open, isEmbedded, i18n]);

  useEffect(() => {
    if (isEmbedded) return undefined;
    const onOpen = async (event) => {
      const detail = event?.detail || {};
      let activeRole = "player";
      try {
        const user = JSON.parse(localStorage.getItem("user") || "null");
        activeRole = readActiveHomeRole(user) || "player";
      } catch {
        activeRole = "player";
      }
      const role = resolveDigipingOpenRole({
        requestedRole: detail.home_role || null,
        activeRole,
        profilingIntro: detail.profilingIntro || null,
      });
      const intro = sanitizeProfilingIntroForRole(role, detail.profilingIntro || null);
      const dropPlayerOnlySeed = Boolean(detail.profilingIntro && !intro);
      setHomeRole(role);
      seedSentRef.current = false;
      setMainTab(detail.tab === "people" ? "people" : "digiping");
      setPeopleInitialGroupId(detail.grId || null);
      setCoachOpenBriefDone(false);
      setProfilingIntro(intro);
      setInviteRosterOpen(false);
      setClubSetupInviteUnlocked(false);
      setProfilingMode(
        isTechnicalProfilingIntro(intro)
          ? "technical"
          : intro === "clubSetup"
            ? "clubSetup"
            : intro === "clubScforh"
              ? "clubScforh"
              : null
      );
      hiddenSeedTurnRef.current = false;
      rewindTurnsRef.current = null;
      setEditingProfilingAnswerId(null);

      const hasSeed =
        !dropPlayerOnlySeed &&
        Boolean(detail.seedMessage || detail.insightText);
      if (hasSeed) {
        const existingId = conversationIdRef.current;
        if (existingId) {
          try {
            await clearConversation(existingId);
          } catch {}
        }
        setConversationId(null);
        setStreamingMsgId(null);
        setMessages(
          isTechnicalProfilingIntro(intro) ||
            intro === "smartGoals" ||
            intro === "trainingAxes" ||
            intro === "clubJoin" ||
            intro === "clubSetup" ||
            intro === "clubScforh"
            ? []
            : [
                {
                  id: `welcome_${Date.now()}`,
                  role: "bot",
                  text: welcomeForRole(role),
                  timestamp: new Date(),
                },
              ]
        );
        setPendingSeed({
          text: detail.seedMessage || detail.insightText || "",
          visible: detail.seedVisible !== false,
          seedKey: detail.seedKey || null,
          seedParams: detail.seedParams || null,
        });
      } else {
        // Opening as staff (or without a seed) must not keep a previous player seed/thread.
        if (isStaffHomeRole(role) || dropPlayerOnlySeed) {
          const existingId = conversationIdRef.current;
          if (existingId) {
            try {
              await clearConversation(existingId);
            } catch {}
          }
          setConversationId(null);
          setStreamingMsgId(null);
          setMessages([]);
        }
        setPendingSeed(null);
      }
      requestLiveFullscreen();
      if (detail.composerPrefill) {
        setInput(detail.composerPrefill);
        setComposerOpen(true);
      }
      setInternalOpen(true);
    };
    window.addEventListener(OPEN_AI_COACH_EVENT, onOpen);
    const onClose = () => {
      if (isEmbedded) return;
      setSmartProfileHandoff(false);
      setInternalOpen(false);
    };
    window.addEventListener(CLOSE_AI_COACH_EVENT, onClose);
    const onRole = () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "null");
        const next = readActiveHomeRole(user);
        setHomeRole(next);
        setConversationId(null);
        setStreamingMsgId(null);
        setMessages([]);
        seedSentRef.current = false;
        setPendingSeed(null);
        setCoachOpenBriefDone(false);
        setProfilingMode(null);
        setProfilingIntro(null);
        setEditingProfilingAnswerId(null);
        briefFingerprintRef.current = "";
      } catch {}
    };
    window.addEventListener("smarttt-home-role-changed", onRole);
    return () => {
      window.removeEventListener(OPEN_AI_COACH_EVENT, onOpen);
      window.removeEventListener(CLOSE_AI_COACH_EVENT, onClose);
      window.removeEventListener("smarttt-home-role-changed", onRole);
    };
  }, [welcomeForRole, isEmbedded]);

  // Embedded Live Training: bootstrap a session-plan design seed when the plan sheet opens Digiping.
  useEffect(() => {
    if (!isEmbedded || !open || !bootstrapSeed) {
      if (!open) bootstrapSeedFpRef.current = "";
      return undefined;
    }
    const fp = JSON.stringify({
      text: bootstrapSeed.text || "",
      seedKey: bootstrapSeed.seedKey || "",
      seedParams: bootstrapSeed.seedParams || null,
      token: bootstrapSeed.token || "",
    });
    if (bootstrapSeedFpRef.current === fp) return undefined;
    bootstrapSeedFpRef.current = fp;

    let cancelled = false;
    (async () => {
      const role =
        bootstrapSeed.home_role ||
        readActiveHomeRole(readStoredUser()) ||
        "coach";
      setHomeRole(role);
      setMainTab("digiping");
      seedSentRef.current = false;
      setProfilingIntro(null);
      setProfilingMode(null);
      const existingId = conversationIdRef.current;
      if (existingId) {
        try {
          await clearConversation(existingId);
        } catch {
          /* ignore */
        }
      }
      if (cancelled) return;
      setConversationId(null);
      setStreamingMsgId(null);
      setMessages([
        {
          id: `welcome_${Date.now()}`,
          role: "bot",
          text: welcomeForRole(role),
          timestamp: new Date(),
        },
      ]);
      setPendingSeed({
        text: bootstrapSeed.text || "",
        visible: bootstrapSeed.visible !== false,
        seedKey: bootstrapSeed.seedKey || null,
        seedParams: bootstrapSeed.seedParams || null,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [isEmbedded, open, bootstrapSeed, welcomeForRole]);

  useEffect(() => {
    if (
      !open ||
      messages.length > 0 ||
      pendingSeed ||
      profilingIntro ||
      profilingMode === "technical" ||
      profilingMode === "clubSetup" ||
      profilingMode === "clubScforh"
    ) {
      return undefined;
    }
    // Only greet on Digiping tab (people pane has its own empty state).
    if (!isEmbedded && mainTab !== "digiping") return undefined;
    const role = homeRole || "player";
    const id = `welcome_${Date.now()}`;
    setMessages([
      {
        id,
        role: "bot",
        text: welcomeForRole(role === "admin" ? "admin" : role),
        timestamp: new Date(),
      },
    ]);
    setStreamingMsgId(id);

    // Persist first-time coach flag without blocking subsequent greets.
    if (
      (role === "coach" || role === "admin") &&
      smartProfile &&
      !smartProfile.coachWelcomeSeen &&
      !coachWelcomeHandled
    ) {
      setCoachWelcomeHandled(true);
      (async () => {
        try {
          await saveSmartProfile({ coachWelcomeSeen: true, lang: lang2 });
          setSmartProfile((prev) =>
            prev ? { ...prev, coachWelcomeSeen: true } : prev
          );
        } catch (_) {
          /* ignore */
        }
      })();
    }
    return undefined;
  }, [
    open,
    messages.length,
    welcomeForRole,
    pendingSeed,
    homeRole,
    smartProfile,
    coachWelcomeHandled,
    lang2,
    mainTab,
    isEmbedded,
    profilingIntro,
    profilingMode,
  ]);

  // Coach open: only write about validations still open (skip already approved/rejected).
  useEffect(() => {
    if (!open || mainTab !== "digiping" || profilingIntro) return undefined;
    if (planAssistMode) return undefined;
    if (profilingMode === "technical" || profilingMode === "clubSetup" || profilingMode === "clubScforh") return undefined;
    if (homeRole !== "coach") return undefined;
    if (pendingSeed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const activeClubId =
          inviteClubId ||
          readStoredUser()?.club_id ||
          readStoredUser()?.club_details?.club_id ||
          brand?.club_id ||
          null;
        const brief = await fetchCoachOpenBrief(lang2, {
          clubId: activeClubId,
        });
        if (cancelled || !brief) return;
        const ids = (brief.items || [])
          .map((i) => i.event_id)
          .filter(Boolean)
          .join(",");
        const firstId = brief.firstAnalysis?.event_id || "";
        const fp = `${brief.count || 0}|${ids}|${firstId}`;
        const wasFirst = !briefFingerprintRef.current;
        setCoachOpenBriefDone(true);
        if (fp === briefFingerprintRef.current) return;
        briefFingerprintRef.current = fp;

        const nextMsgs = [];
        if (brief.summaryText) {
          nextMsgs.push({
            id: `coach_summary_${ids || "none"}`,
            role: "bot",
            text: brief.summaryText,
            timestamp: new Date(),
          });
        }
        if (brief.firstAnalysis?.text && firstId) {
          nextMsgs.push({
            id: `coach_first_${firstId}`,
            role: "bot",
            text: brief.firstAnalysis.text,
            timestamp: new Date(),
          });
          if (brief.firstAnalysis?.player_user_id) {
            setSelectedPlayerId(brief.firstAnalysis.player_user_id);
            setOpenPeerOnce(brief.firstAnalysis.player_user_id);
          }
        }

        setMessages((prev) => {
          const withoutBrief = prev.filter((m) => {
            const id = String(m.id || "");
            return (
              !id.startsWith("coach_summary_") && !id.startsWith("coach_first_")
            );
          });
          if (!nextMsgs.length) return withoutBrief;
          const welcome = withoutBrief.filter((m) =>
            String(m.id || "").startsWith("welcome_")
          );
          const rest = withoutBrief.filter(
            (m) => !String(m.id || "").startsWith("welcome_")
          );
          return [...welcome, ...nextMsgs, ...rest];
        });
        if (wasFirst && nextMsgs.length) {
          const lastId = nextMsgs[nextMsgs.length - 1]?.id;
          if (lastId) setStreamingMsgId(lastId);
        }
        refreshSmart();
      } catch (_) {
        setCoachOpenBriefDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    mainTab,
    homeRole,
    pendingSeed,
    lang2,
    refreshSmart,
    profilingIntro,
    planAssistMode,
    profilingMode,
    inviteClubId,
    brand?.club_id,
  ]);

  const applyVoiceDraft = useCallback((cleaned, { language = null, finalize = false } = {}) => {
    if (!cleaned && !finalize) return;
    if (!composerOpenRef.current) skipComposerFocusRef.current = true;
    setDraftLocked(true);
    if (cleaned) {
      setInput(cleaned);
      setVoiceInputMode(true);
    }
    if (language) setVoiceLanguage(toCoachLanguage(language));
    setComposerOpen(true);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.readOnly = true;
      if (finalize) dismissVirtualKeyboard(el);
      resizeComposerTextarea(el);
      el.scrollTop = el.scrollHeight;
      if (finalize) {
        window.setTimeout(() => {
          const again = inputRef.current;
          if (again && document.activeElement !== again) again.readOnly = true;
        }, 120);
      }
    });
  }, []);

  const handleVoicePartial = useCallback(
    (text) => {
      if (!voiceLiveRef.current) return;
      const cleaned = (text || "").trim();
      if (!cleaned) return;
      applyVoiceDraft(cleaned);
    },
    [applyVoiceDraft]
  );

  const handleVoiceTranscript = useCallback(
    ({ text, language }) => {
      voiceLiveRef.current = false;
      if (voicePhaseRef.current === "live") voicePhaseRef.current = "final";
      const cleaned = (text || "").trim();
      if (!cleaned) {
        setVoiceSendBlocked(false);
        return;
      }
      // After stop: show the transcript without opening the on-screen keyboard.
      applyVoiceDraft(cleaned, { language, finalize: true });
      setVoiceSendBlocked(false);
    },
    [applyVoiceDraft]
  );

  const handleVoiceListeningChange = useCallback((listening) => {
    const on = Boolean(listening);
    if (on) {
      if (voicePhaseRef.current === "live" || voicePhaseRef.current === "final") return;
      voicePhaseRef.current = "live";
      // Fresh STT buffer for this take (same as Cursor: restart clears prior interim).
      voiceLiveRef.current = true;
      setVoiceSendBlocked(true);
      if (!composerOpenRef.current) skipComposerFocusRef.current = true;
      setDraftLocked(true);
      setInput("");
      setVoiceInputMode(true);
      setComposerOpen(true);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.readOnly = true;
        resizeComposerTextarea(el);
      });
      return;
    }
    // Keep Send blocked until onFinal validates (PROCESSING gap after stop).
    voiceLiveRef.current = false;
    voicePhaseRef.current = "idle";
  }, []);

  const pushBotReply = (body) => {
    const botId = `bot_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: botId,
        role: "bot",
        text: body,
        timestamp: new Date(),
        agentId: activeAgent.id,
        agentName: activeAgent.name,
      },
    ]);
    setStreamingMsgId(botId);
  };

  const handleFixcode = async (text) => {
    cancelTourSpeech();
    const parsed = parseFixcode(text);
    setMessages((prev) => [
      ...prev,
      {
        id: `user_${Date.now()}`,
        role: "user",
        text,
        timestamp: new Date(),
      },
    ]);
    setInput("");
    dismissVirtualKeyboard(inputRef.current);
    setLoading(true);
    try {
      if (!parsed.ok) {
        pushBotReply(t("fixcodeHelp"));
        return;
      }
      const ctx = getContext();
      const result = await submitFixcode({
        text,
        page_path: `${location.pathname}${location.search || ""}`,
        home_role: ctx.home_role,
        language: ctx.language,
        club_id: ctx.club_id,
        club_name: ctx.club_name,
        agent_id: ctx.agent_id,
        ts_id: ctx.ts_id,
        te_id: ctx.te_id,
      });
      const kindLabel = t(`fixcodeKind_${result.kind}`, result.kind);
      pushBotReply(
        result.queued
          ? t("fixcodeQueued", { kind: kindLabel, id: result.request_id })
          : t("fixcodeLogged", { kind: kindLabel, id: result.request_id })
      );
    } catch (error) {
      pushBotReply(
        `⚠️ ${error.message || t("fixcodeFailed", "Could not log this /fixcode message.")}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (overrideText, options = {}) => {
    const fromField = String(inputRef.current?.value || "");
    const text = (overrideText != null ? overrideText : input || fromField).trim();
    // Composer Send must wait for final STT; programmatic overrideText may still send.
    if (!text || loading || (overrideText == null && voiceSendBlocked)) return;
    if (streamingMsgId) setStreamingMsgId(null);
    if (isFixcodeCommand(text)) {
      await handleFixcode(text);
      return;
    }
    cancelTourSpeech();

    // Detect if the player wrote in a specific language; if so, switch app localization & Pingflo context
    const detectedLang = detectInputLanguage(text);
    if (detectedLang && detectedLang !== currentAppLanguage(i18n)) {
      applyAppLanguage(i18n, detectedLang);
    }

    const hideUserMessage = options.hideUserMessage === true;
    const answeringBotId = latestBotMessageRef.current?.id || null;
    const latestPrompt = latestBotMessageRef.current?.profilingPrompt;
    const profilingAnswer =
      options.profilingAnswer ||
      (profilingMode === "technical" &&
      latestPrompt?.cbKey &&
      latestPrompt?.questionType !== "conversation_offer" &&
      latestPrompt?.questionType !== "exercise_choice"
        ? {
            cbKey: latestPrompt.cbKey,
            questionType: latestPrompt.questionType,
            label: text,
            questionText: latestBotMessageRef.current?.text || "",
            answerOptions: latestPrompt.answerOptions,
          }
        : null);
    if (hideUserMessage && profilingMode === "technical") {
      hiddenSeedTurnRef.current = true;
    }
    if (options.autoevalAnswer || !hideUserMessage) {
      const userMsg = hideUserMessage
        ? null
        : {
            id: `user_${Date.now()}`,
            role: "user",
            text,
            timestamp: new Date(),
            editable: profilingMode === "technical",
            answersBotId: profilingMode === "technical" ? answeringBotId : null,
          };
      setMessages((prev) => {
        let next = prev;
        if (options.autoevalAnswer) {
          next = next.map((m) =>
            m.autoevalGuide && !m.autoevalSubmitted
              ? { ...m, autoevalSubmitted: true }
              : m
          );
        }
        return userMsg ? [...next, userMsg] : next;
      });
    }
    if (overrideText == null) {
      // Validated/sent — reset transcription buffer so the next mic take starts empty.
      voiceLiveRef.current = false;
      setVoiceSendBlocked(false);
      setInput("");
      setDraftLocked(false);
    }
    const wasVoice = voiceInputMode && overrideText == null;
    const voiceLang = voiceLanguage;
    setVoiceInputMode(false);
    setVoiceLanguage(null);
    if (overrideText == null) {
      // Drop the on-screen keyboard so Pingflo's reply is readable.
      dismissVirtualKeyboard(inputRef.current);
    }
    setEditingProfilingAnswerId(null);
    setLoading(true);

    const rewindCompleteTurns = rewindTurnsRef.current;
    rewindTurnsRef.current = null;

    try {
      const context = getContext();
      if (profilingAnswer) {
        context.profilingAnswer = profilingAnswer;
      }
      if (options.autoevalAnswer) {
        context.autoevalAnswer = options.autoevalAnswer;
      }
      if (detectedLang) {
        context.language = detectedLang;
      } else if (wasVoice && voiceLang) {
        context.language = voiceLang;
      }
      const result = await sendMessage(text, conversationId, context, {
        input_mode: wasVoice ? "voice" : "text",
        ...(Number.isFinite(rewindCompleteTurns) ? { rewindCompleteTurns } : {}),
      });

      if (result.conversationId && !conversationId) {
        setConversationId(result.conversationId);
      }

      const botId = `bot_${Date.now()}`;
      const dim =
        profilingMode === "technical"
          ? null
          : result.smart?.activeDimension ||
            result.actionResult?.dimension ||
            null;
      const dimLabel = dim
        ? dimensionLabels[dim]?.label || result.smart?.labels?.[dim]?.label
        : null;
      const autoevalGuide =
        result.autoevalGuide ||
        (result.actionResult?.type === "autoeval_guide" ? result.actionResult.guide : null);
      setMessages((prev) => [
        ...prev,
        {
          id: botId,
          role: "bot",
          text: result.message,
          timestamp: new Date(),
          dimension: dim,
          dimensionLabel: dimLabel,
          agentId: result.agent?.id || activeAgent.id,
          agentName: result.agent?.name || activeAgent.name,
          profilingPrompt: result.profilingPrompt || null,
          autoevalGuide: autoevalGuide || null,
        },
      ]);
      setStreamingMsgId(botId);

      if (context.page === "planning" || context.session_plan || result.planningMutated) {
        requestPlanningRefresh();
        if (
          context.session_plan ||
          String(location.pathname || "").toLowerCase().includes("live-training")
        ) {
          try {
            window.dispatchEvent(new CustomEvent("smarttt:live-plan-refresh"));
          } catch {
            /* ignore */
          }
        }
      }

      if (result.actionResult?.type === "presence_updated") {
        try {
          window.dispatchEvent(new CustomEvent("smarttt:live-plan-refresh"));
          window.dispatchEvent(
            new CustomEvent("smarttt:presence-updated", {
              detail: result.actionResult,
            })
          );
        } catch {
          /* ignore */
        }
      }

      if (result.smart?.status) {
        setSmartProfile((prev) => ({
          ...(prev || {}),
          status: result.smart.status,
          coachPendingDims: result.smart.coachPendingDims || prev?.coachPendingDims,
          labels: result.smart.labels || prev?.labels,
          summaries: result.smart.summaries || prev?.summaries,
          activeDimension: result.smart.activeDimension || prev?.activeDimension,
        }));
      }
      if (result.actionResult?.type === "smart_coach_notify") {
        refreshSmart();
      }
      if (result.actionResult?.type === "ui_guide" && result.actionResult.playbook_id) {
        startPingfloTour({
          playbookId: result.actionResult.playbook_id,
          mode: result.actionResult.mode === "do" ? "do" : "show",
          role: homeRole,
        });
      }

      if (result.profilingJustCompleted) {
        try {
          const stored = JSON.parse(localStorage.getItem("user") || "{}");
          stored.isCompetencyblocDone = true;
          localStorage.setItem("user", JSON.stringify(stored));
          window.dispatchEvent(
            new CustomEvent("smarttt:user-profile-updated", {
              detail: { isCompetencyblocDone: true },
            })
          );
        } catch (_) {
          /* ignore */
        }
        setProfilingMode(null);
        seedSentRef.current = false;
        if (result.postProfiling?.phase === "training_axes") {
          setPendingSeed({
            text: t("digipingTrainingAxesHomeSeed"),
            visible: false,
          });
          setProfilingIntro("trainingAxes");
        } else {
          setPendingSeed({
            text: t("digipingSmartContinueSeed"),
            visible: false,
          });
          setProfilingIntro("smartGoals");
        }
        refreshSmart();
      } else if (
        !planAssistMode &&
        (result.openSmartProfile ||
          (homeRole === "player" &&
            /bon\s+entra[iî]nement|sera notifi[eé].*validation|coach will be notified.*validation|enjoy your training/i.test(
              String(result.message || "")
            ) &&
            (result.postProfiling?.technicalDone ||
              result.actionResult?.type === "exercise_proposal")))
      ) {
        refreshSmart();
        setProfilingIntro(null);
        setProfilingMode(null);
        // Wait for the player to finish reading, then they confirm opening SMART.
        setSmartProfileHandoff(true);
      } else if (result.postProfiling && !result.postProfiling.pending) {
        refreshSmart();
      }

      if (result.actionResult?.type === "group_created" && result.actionResult?.gr_id) {
        navigate("/GroupList", { state: { openGroupId: result.actionResult.gr_id } });
      }
      if (result.actionResult?.type === "app_language_changed" && result.actionResult.language) {
        applyAppLanguage(i18n, result.actionResult.language);
      }
    } catch (error) {
      const botId = `bot_err_${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: botId,
          role: "bot",
          text: `⚠️ ${error.message || "Sorry, something went wrong. Please try again."}`,
          timestamp: new Date(),
        },
      ]);
      setStreamingMsgId(botId);
    } finally {
      setLoading(false);
    }
  };
  handleSendRef.current = handleSend;

  const handleAutoevalSubmit = useCallback((payload) => {
    if (!payload?.te_id) return;
    handleSendRef.current?.(payload.label, {
      autoevalAnswer: {
        te_id: payload.te_id,
        exercise_perf: payload.exercise_perf,
        perceived_exertion: payload.perceived_exertion,
      },
    });
  }, []);

  const handleInvitesSent = (data) => {
    const sent = Array.isArray(data?.sent) ? data.sent : [];
    const failed = Array.isArray(data?.failed) ? data.failed : [];
    const sentLines = sent.map((row) => {
      const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
      const extra = row.already_member
        ? " (already a member)"
        : row.associated
          ? " (linked existing account — Digiping will review SMART)"
          : "";
      return `- ${name || "Player"} · ${row.email}${extra}`;
    });
    const failLines = failed.map(
      (row) => `- ${row.email}${row.reason ? ` (${row.reason})` : ""}`
    );
    const text = [
      sent.length
        ? `I invited ${sent.length} player${sent.length === 1 ? "" : "s"} to register at the sports club and complete player profiling:`
        : "I tried to invite players, but none were sent.",
      sentLines.join("\n"),
      failLines.length ? `Could not invite:\n${failLines.join("\n")}` : "",
      "Do not invent emails. Confirm who was invited, then continue club setup if halls and groups are ready.",
    ]
      .filter(Boolean)
      .join("\n");
    handleSend(text);
  };

  const handleSpielplanAttach = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || loading || streamingMsgId) return;
    setLoading(true);
    try {
      const data = await importLeagueSpielplan({ file });
      const fmt = data?.format || "spielplan";
      const count = data?.fixture_count ?? 0;
      const days = data?.matchday_count ?? 0;
      const ver = data?.source_version ? ` version ${data.source_version}` : "";
      const isLgett = String(fmt).toLowerCase() === "lgett";
      const summary =
        lang2 === "fr"
          ? isLgett
            ? `J'ai importé le calendrier LGETT (${fmt}) : ${count} matchs, ${days} journées${ver}. Résume les prochains matchs de notre club.`
            : `J'ai importé le Spielplan FLTT (${fmt}) : ${count} matchs, ${days} journées${ver}. Résume les prochains matchs de notre club. Si ce n'était que NL 2+, rappelle que l'équipe 1 / NL 1 utilise le PDF National TT League.`
          : isLgett
            ? `I imported the LGETT calendar (${fmt}): ${count} fixtures, ${days} matchdays${ver}. Summarize our club's upcoming championship matches.`
            : `I imported the FLTT Spielplan (${fmt}): ${count} fixtures, ${days} matchdays${ver}. Summarize our club's upcoming championship matches. If this was NL 2+ only, remind me that Team 1 / NL 1 uses the National TT League PDF.`;
      setLoading(false);
      await handleSend(summary);
    } catch (err) {
      setLoading(false);
      window.alert(err?.response?.data?.message || err.message || "Import failed");
    }
  };

  useEffect(() => {
    // If we have a pending seed, wait until the user dismisses the technical intro
    // so the first technical question is triggered on click.
    if (!open || !pendingSeed || loading || seedSentRef.current || profilingIntro)
      return;
    seedSentRef.current = true;
    if (pendingSeed?.visible === false && (profilingMode === "technical" || profilingMode === "clubSetup" || profilingMode === "clubScforh" || isTechnicalProfilingIntro(profilingIntro) || profilingIntro === "smartGoals" || profilingIntro === "trainingAxes" || profilingIntro === "clubJoin")) {
      hiddenSeedTurnRef.current = true;
    }
    const seed = pendingSeed;
    setPendingSeed(null);
    // Re-resolve i18n seeds in the current app language (club-country seed may be stale).
    const seedText =
      seed?.seedKey
        ? t(seed.seedKey, { ...(seed.seedParams || {}), lng: digipingLang })
        : seed?.text || "";
    handleSend(seedText, {
      hideUserMessage: seed?.visible === false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingSeed, loading, profilingIntro]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    cancelTourSpeech();
    if (conversationId) {
      await clearConversation(conversationId);
    }
    setConversationId(null);
    setStreamingMsgId(null);
    setMessages([]);
    seedSentRef.current = false;
    setPendingSeed(null);
    setCoachOpenBriefDone(false);
    briefFingerprintRef.current = "";
    setProfilingIntro(null);
    setProfilingMode(null);
    hiddenSeedTurnRef.current = false;
    rewindTurnsRef.current = null;
    setEditingProfilingAnswerId(null);
    setSmartProfileHandoff(false);
    setInviteRosterOpen(false);
    setClubSetupInviteUnlocked(false);
  };

  const handleQuickAction = (text) => {
    setInput(text);
    setComposerOpen(true);
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 80);
  };

  const statusLabel = loading
    ? `${activeAgent?.name || "Pingflo"} is thinking…`
    : streamingMsgId
      ? `${activeAgent?.name || "Pingflo"} is typing…`
      : null;

  const latestBotMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg?.role !== "bot") continue;
      if (String(msg?.id || "").startsWith("welcome_")) continue;
      return msg;
    }
    return null;
  }, [messages]);

  useEffect(() => {
    latestBotMessageRef.current = latestBotMessage;
  }, [latestBotMessage]);

  useEffect(() => {
    if (!canInvitePlayers) {
      setInviteRosterOpen(false);
      setClubSetupInviteUnlocked(false);
    }
  }, [canInvitePlayers]);

  useEffect(() => {
    if (!canInvitePlayers) return;
    if (profilingMode !== "clubSetup" && profilingMode !== "clubScforh") return;
    const offerKind = latestBotMessage?.profilingPrompt?.offerKind;
    if (
      offerKind === "admin_invite_players" ||
      looksLikeAdminInvitePlayersQuestion(latestBotMessage?.text)
    ) {
      setClubSetupInviteUnlocked(true);
      setInviteRosterOpen(true);
    }
  }, [canInvitePlayers, profilingMode, latestBotMessage]);

  const editAnswerLabel = t("digipingEditAnswer");
  const editingHintLabel = t("digipingEditingHint");

  const beginEditProfilingAnswer = useCallback(
    (userMsgId) => {
      if (!userMsgId || loading || streamingMsgId) return;
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === userMsgId);
        if (idx < 0) return prev;
        const truncated = prev.slice(0, idx);
        const visibleUserCount = truncated.filter((m) => m.role === "user").length;
        rewindTurnsRef.current =
          (hiddenSeedTurnRef.current ? 1 : 0) + visibleUserCount;
        setEditingProfilingAnswerId(userMsgId);
        return truncated;
      });
      setStreamingMsgId(null);
      setTimeout(() => {
        scrollChatHistoryToEnd(messagesEndRef.current, "smooth");
      }, 80);
    },
    [loading, streamingMsgId]
  );

  const technicalProfilingOptions = useMemo(() => {
    if (profilingIntro) return [];
    // Quick-answer chips are for player/technical (and club setup) profiling only —
    // not for free coaching chat on planning / live session design.
    const profilingChipMode =
      profilingMode === "technical" ||
      profilingMode === "clubSetup" ||
      profilingMode === "clubScforh";
    if (!profilingChipMode) return [];

    const lngOpts = homeRole === "admin" ? { lng: digipingLang } : undefined;
    const prompt = latestBotMessage?.profilingPrompt;
    const questionText = latestBotMessage?.text || "";
    const backendOptions = prompt?.answerOptions;
    const overlayIfNeeded = (options) =>
      shouldOverlayProfilingChips(prompt)
        ? overlayNumberedProfilingChips(options, questionText)
        : options;
    if (Array.isArray(backendOptions) && backendOptions.length > 0) {
      return overlayIfNeeded(backendOptions);
    }
    if (
      homeRole === "admin" &&
      looksLikeAdminConfirmQuestion(latestBotMessage?.text)
    ) {
      return [
        { value: "yes", label: t("digipingChipYes", lngOpts) || "Yes" },
        { value: "no", label: t("digipingChipNo", lngOpts) || "No" },
        {
          value: "add_precision",
          label: t("other", lngOpts) || "Other",
          action: "open_composer",
        },
      ];
    }
    if (profilingMode === "technical" && shouldOverlayProfilingChips(prompt)) {
      return overlayNumberedProfilingChips([], questionText);
    }
    return [];
  }, [profilingMode, profilingIntro, latestBotMessage, homeRole, digipingLang, t]);

  const composerPlaceholder = useMemo(() => {
    const lngOpts = homeRole === "admin" ? { lng: digipingLang } : undefined;
    if (editingProfilingAnswerId) {
      return t("aiCoachPlaceholderEditing", lngOpts);
    }
    const prompt = latestBotMessage?.profilingPrompt;
    const questionType = prompt?.questionType;
    if (questionType === "exercise_choice") {
      return t("aiCoachPlaceholderExerciseChoice", lngOpts);
    }
    if (questionType === "conversation_offer") {
      if (prompt?.offerKind === "session_ready") {
        return t("aiCoachPlaceholderSessionReady", lngOpts);
      }
      return t("aiCoachPlaceholderOffer", lngOpts);
    }
    if (
      profilingMode === "technical" ||
      technicalProfilingOptions.length > 0 ||
      Boolean(prompt)
    ) {
      return t("aiCoachPlaceholderProfiling", lngOpts);
    }
    return t("aiCoachPlaceholder", lngOpts);
  }, [
    editingProfilingAnswerId,
    latestBotMessage,
    profilingMode,
    technicalProfilingOptions.length,
    t,
    homeRole,
    digipingLang,
  ]);

  const handleProfilingOptionClick = useCallback(
    (option) => {
      if (option?.action === "open_invite_roster" || option?.value === "open_invite_list") {
        if (!canManageChatPlayerInvites(homeRole)) return;
        setInviteRosterOpen(true);
        setClubSetupInviteUnlocked(true);
        return;
      }
      if (option?.action === "open_composer") {
        setComposerOpen(true);
        setInput("");
        // Focus opens the on-screen keyboard on mobile; slight delay after layout.
        window.setTimeout(() => {
          const el = inputRef.current;
          if (!el) return;
          el.focus({ preventScroll: true });
        }, 120);
        return;
      }
      if (option?.action === "go_home" || option?.value === "go_home") {
        setSmartProfileHandoff(false);
        setOpen(false);
        navigate("/home", { replace: true });
        return;
      }
      if (
        option?.action === "open_smart_profile" ||
        option?.value === "open_smart_profile"
      ) {
        setSmartProfileHandoff(false);
        setOpen(false);
        navigate("/profile", { state: { tab: "smart" } });
        return;
      }
      const prompt = latestBotMessageRef.current?.profilingPrompt;
      const questionText = latestBotMessageRef.current?.text || "";
      const displayedOptions = shouldOverlayProfilingChips(prompt)
        ? overlayNumberedProfilingChips(prompt?.answerOptions, questionText)
        : prompt?.answerOptions;
      handleSendRef.current?.(option?.label || (typeof option === "string" ? option : ""), {
        profilingAnswer: prompt?.cbKey
          ? {
              cbKey: prompt.cbKey,
              questionType: prompt.questionType,
              value: option?.value || option?.label,
              label: option?.label || (typeof option === "string" ? option : ""),
              questionText,
              answerOptions: displayedOptions || prompt.answerOptions,
            }
          : null,
      });
    },
    [navigate, setOpen, homeRole]
  );

  const technicalIntroCard =
    isTechnicalProfilingIntro(profilingIntro) ? (
      <div className="digiping-tech-intro">
        <div className="digiping-tech-intro__card">
          <div className="digiping-tech-intro__header">
            <div className="logo-badge logo-badge--digiping">
              <DigipingMark size={34} />
            </div>
            <div className="wizard-intro-titles">
              <span className="digiping-tag">{digipingTag}</span>
              <h2>
                {profilingIntro === "technicalUpdate"
                  ? t("digipingTechUpdateTitle")
                  : t("digipingTechProfileTitle")}
              </h2>
            </div>
          </div>
          <div className="digiping-tech-intro__scroll">
          <p className="intro-body-text">
            <RichText
              text={
                profilingIntro === "technicalUpdate"
                  ? t("digipingTechUpdateBody")
                  : t("digipingTechProfileBody")
              }
            />
          </p>
          <div className="smart-grid">
            <div className="smart-bullet smart-bullet--s">
              <span className="smart-letter" aria-hidden="true">1</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingTechBullet1Label")}</span>
                <span className="smart-desc">{t("digipingTechBullet1Desc")}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--m">
              <span className="smart-letter" aria-hidden="true">2</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingTechBullet2Label")}</span>
                <span className="smart-desc">{t("digipingTechBullet2Desc")}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--a">
              <span className="smart-letter" aria-hidden="true">3</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingTechBullet3Label")}</span>
                <span className="smart-desc">{t("digipingTechBullet3Desc")}</span>
              </div>
            </div>
          </div>
          </div>
          <button
            type="button"
            className="wizard-begin-btn ripple"
            onClick={() => setProfilingIntro(null)}
          >
            {profilingIntro === "technicalUpdate"
              ? t("digipingTechUpdateStart")
              : t("digipingTechStart")}
          </button>
        </div>
      </div>
    ) : null;

  const smartGoalsIntroCard =
    profilingIntro === "smartGoals" ? (
      <div className="digiping-tech-intro">
        <div className="digiping-tech-intro__card">
          <div className="digiping-tech-intro__header">
            <div className="logo-badge logo-badge--digiping">
              <DigipingMark size={34} />
            </div>
            <div className="wizard-intro-titles">
              <span className="digiping-tag">{digipingTag}</span>
              <h2>{t("digipingSmartGoalsTitle")}</h2>
            </div>
          </div>
          <div className="digiping-tech-intro__scroll">
          <p className="intro-body-text">
            <RichText text={t("digipingSmartGoalsBody")} />
          </p>
          <div className="smart-grid">
            <div className="smart-bullet smart-bullet--s">
              <span className="smart-letter" aria-hidden="true">S</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingSmartPrimaryLabel")}</span>
                <span className="smart-desc">{t("digipingSmartPrimaryDesc")}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--m">
              <span className="smart-letter" aria-hidden="true">·</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingSmartSecondaryLabel")}</span>
                <span className="smart-desc">{t("digipingSmartSecondaryDesc")}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--t">
              <span className="smart-letter" aria-hidden="true">T</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingSmartCycleLabel")}</span>
                <span className="smart-desc">{t("digipingSmartCycleDesc")}</span>
              </div>
            </div>
          </div>
          </div>
          <button
            type="button"
            className="wizard-begin-btn ripple"
            onClick={() => setProfilingIntro(null)}
          >
            {t("digipingSmartContinue")}
          </button>
        </div>
      </div>
    ) : null;

  const trainingAxesIntroCard =
    profilingIntro === "trainingAxes" ? (
      <div className="digiping-tech-intro">
        <div className="digiping-tech-intro__card">
          <div className="digiping-tech-intro__header">
            <div className="logo-badge logo-badge--digiping">
              <DigipingMark size={34} />
            </div>
            <div className="wizard-intro-titles">
              <span className="digiping-tag">{digipingTag}</span>
              <h2>{t("digipingTrainingAxesTitle")}</h2>
            </div>
          </div>
          <div className="digiping-tech-intro__scroll">
          <p className="intro-body-text">
            <RichText text={t("digipingTrainingAxesBody")} />
          </p>
          <div className="smart-grid">
            <div className="smart-bullet smart-bullet--s">
              <span className="smart-letter" aria-hidden="true">★</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingSmartPrimaryLabel")}</span>
                <span className="smart-desc">{t("digipingSmartPrimaryDesc")}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--m">
              <span className="smart-letter" aria-hidden="true">·</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingSmartSecondaryLabel")}</span>
                <span className="smart-desc">{t("digipingSmartSecondaryDesc")}</span>
              </div>
            </div>
          </div>
          </div>
          <button
            type="button"
            className="wizard-begin-btn ripple"
            onClick={() => setProfilingIntro(null)}
          >
            {t("digipingSmartContinue")}
          </button>
        </div>
      </div>
    ) : null;

  const clubSetupIntroCard =
    profilingIntro === "clubSetup" ? (
      <div className="digiping-tech-intro">
        <div className="digiping-tech-intro__card">
          <div className="digiping-tech-intro__header">
            <div className="logo-badge logo-badge--digiping">
              <DigipingMark size={34} />
            </div>
            <div className="wizard-intro-titles">
              <span className="digiping-tag">{digipingTag}</span>
              <h2>{t("digipingClubSetupTitle", { lng: digipingLang })}</h2>
            </div>
          </div>
          <p className="intro-body-text">
            <RichText text={t("digipingClubSetupBody", { lng: digipingLang })} />
          </p>
          <div className="digiping-tech-intro__scroll">
          {scforhSeed?.targetGroups?.length ? (
            <ScforhWhoCapsule
              targetGroups={scforhSeed.targetGroups}
              tone="dark"
              variant="compact"
              lng={digipingLang}
            />
          ) : (
            <ScforhWhoCapsule
              bands={["youth", "adults", "seniors"]}
              tone="dark"
              variant="compact"
              lng={digipingLang}
            />
          )}
          <div className="smart-grid">
            <div className="smart-bullet smart-bullet--s">
              <span className="smart-letter" aria-hidden="true">S</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubSetupSmartLabel", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubSetupSmartDesc", { lng: digipingLang })}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--m">
              <span className="smart-letter" aria-hidden="true">1</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubSetupBullet1Label", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubSetupBullet1Desc", { lng: digipingLang })}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--a">
              <span className="smart-letter" aria-hidden="true">2</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubSetupBullet2Label", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubSetupBullet2Desc", { lng: digipingLang })}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--r">
              <span className="smart-letter" aria-hidden="true">3</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubSetupBullet3Label", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubSetupBullet3Desc", { lng: digipingLang })}</span>
              </div>
            </div>
          </div>
          </div>
          <button
            type="button"
            className="wizard-begin-btn ripple"
            onClick={() => {
              setPendingSeed((prev) => ({
                text: prev?.text || "",
                visible: false,
                seedKey: prev?.seedKey || "digipingClubSetupHomeSeed",
                seedParams: prev?.seedParams || null,
              }));
              setProfilingIntro(null);
            }}
          >
            {t("digipingClubSetupStart", { lng: digipingLang })}
          </button>
        </div>
      </div>
    ) : null;

  const clubScforhIntroCard =
    profilingIntro === "clubScforh" ? (
      <div className="digiping-tech-intro">
        <div className="digiping-tech-intro__card">
          <div className="digiping-tech-intro__header">
            <div className="logo-badge logo-badge--digiping">
              <DigipingMark size={34} />
            </div>
            <div className="wizard-intro-titles">
              <span className="digiping-tag">{digipingTag}</span>
              <h2>{t("digipingClubScforhTitle", { lng: digipingLang })}</h2>
            </div>
          </div>
          <p className="intro-body-text">
            <RichText text={t("digipingClubScforhExplain", { lng: digipingLang })} />
          </p>
          <div className="digiping-tech-intro__scroll">
          <div className="smart-grid">
            <div className="smart-bullet smart-bullet--s">
              <span className="smart-letter" aria-hidden="true">S</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubScforhBullet1Label", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubScforhBullet1Desc", { lng: digipingLang })}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--m">
              <span className="smart-letter" aria-hidden="true">M</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubScforhBullet2Label", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubScforhBullet2Desc", { lng: digipingLang })}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--a">
              <span className="smart-letter" aria-hidden="true">A</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubScforhBulletALabel", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubScforhBulletADesc", { lng: digipingLang })}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--r">
              <span className="smart-letter" aria-hidden="true">R</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubScforhBulletRLabel", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubScforhBulletRDesc", { lng: digipingLang })}</span>
              </div>
            </div>
            <div className="smart-bullet smart-bullet--t">
              <span className="smart-letter" aria-hidden="true">T</span>
              <div className="smart-bullet-copy">
                <span className="smart-label">{t("digipingClubScforhBullet3Label", { lng: digipingLang })}</span>
                <span className="smart-desc">{t("digipingClubScforhBullet3Desc", { lng: digipingLang })}</span>
              </div>
            </div>
          </div>
          <details className="parent-profile-scforh-expand digiping-scforh-expand">
            <summary>
              {t("whoCapsuleEyebrow", { lng: digipingLang }) || "WHO · Sports Club for Health"}
            </summary>
            <ScforhWhoCapsule
              targetGroups={scforhSeed?.targetGroups}
              bands={scforhSeed?.targetGroups?.length ? [] : ["youth", "adults", "seniors"]}
              tone="dark"
              variant="compact"
              lng={digipingLang}
            />
          </details>
          </div>
          <button
            type="button"
            className="wizard-begin-btn ripple"
            onClick={() => {
              setPendingSeed((prev) => ({
                text: prev?.text || "",
                visible: false,
                seedKey: prev?.seedKey || "digipingClubScforhHomeSeed",
                seedParams: prev?.seedParams || null,
              }));
              setProfilingIntro(null);
            }}
          >
            {t("digipingClubScforhStart", { lng: digipingLang })}
          </button>
        </div>
      </div>
    ) : null;

  const clubJoinIntroCard =
    profilingIntro === "clubJoin" ? (
      <div className="digiping-tech-intro">
        <div className="digiping-tech-intro__card">
          <div className="digiping-tech-intro__header">
            <div className="logo-badge logo-badge--digiping">
              <DigipingMark size={34} />
            </div>
            <div className="wizard-intro-titles">
              <span className="digiping-tag">{digipingTag}</span>
              <h2>{t("digipingClubJoinTitle")}</h2>
            </div>
          </div>
          <div className="digiping-tech-intro__scroll">
            <p className="intro-body-text">
              <RichText text={t("digipingClubJoinBody")} />
            </p>
            <div className="smart-grid">
              <div className="smart-bullet smart-bullet--s">
                <span className="smart-letter" aria-hidden="true">S</span>
                <div className="smart-bullet-copy">
                  <span className="smart-label">{t("digipingClubJoinBullet1Label")}</span>
                  <span className="smart-desc">{t("digipingClubJoinBullet1Desc")}</span>
                </div>
              </div>
              <div className="smart-bullet smart-bullet--m">
                <span className="smart-letter" aria-hidden="true">M</span>
                <div className="smart-bullet-copy">
                  <span className="smart-label">{t("digipingClubJoinBullet2Label")}</span>
                  <span className="smart-desc">{t("digipingClubJoinBullet2Desc")}</span>
                </div>
              </div>
              <div className="smart-bullet smart-bullet--a">
                <span className="smart-letter" aria-hidden="true">A</span>
                <div className="smart-bullet-copy">
                  <span className="smart-label">{t("digipingClubJoinBullet3Label")}</span>
                  <span className="smart-desc">{t("digipingClubJoinBullet3Desc")}</span>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="wizard-begin-btn ripple"
            onClick={() => setProfilingIntro(null)}
          >
            {t("digipingClubJoinStart")}
          </button>
        </div>
      </div>
    ) : null;

  const panelBody = open ? (
    <>
            {!hideHeader ? (
            <header className="digiping-chat__header">
              <div className="digiping-chat__brand">
                <div className="digiping-chat__avatar digiping-chat__avatar--sm" title={activeAgent.name}>
                  <AgentMark agentId={activeAgent.id} size={28} showGlow />
                </div>
                <div className="digiping-chat__brand-text">
                  <div className="digiping-chat__agent-label-row">
                    <span className="digiping-chat__title" title={headerClubName || activeAgent.name}>
                      {planAssistMode ? "Pingflo" : activeAgent.name}
                    </span>
                    {!planAssistMode ? (
                      <span className="agent-badge-pill" title={t(activeAgent.missionKey, activeAgent.defaultTagline)}>
                        <span className="agent-badge-pill__emoji">{activeAgent.emoji}</span>
                        <span className="agent-badge-pill__text">
                          {t(activeAgent.badgeKey, activeAgent.defaultBadge)}
                        </span>
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="digiping-chat__actions">
                <LanguageShortcutFab variant="invite" />
                <button
                  type="button"
                  className="digiping-chat__icon-btn digiping-chat__clean-btn"
                  onClick={handleClear}
                  title={t("clearChat", "Clear chat")}
                  aria-label={t("clearChat", "Clear chat")}
                >
                  <DeleteSweepIcon fontSize="small" />
                </button>
                {!isEmbedded ? (
                  <button
                    type="button"
                    className="digiping-chat__icon-btn digiping-chat__close-btn"
                    onClick={closeChat}
                    title={t("close", "Close")}
                    aria-label={t("close", "Close")}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                ) : null}
              </div>
            </header>
            ) : null}

            {(onPlanningPage && planningFocus?.station_name) ||
            (sessionContext?.session_plan &&
              (sessionContext?.station_name || sessionContext?.exercise_name)) ? (
              <p className="digiping-chat__planning-chip">
                {[
                  sessionContext?.station_name || planningFocus?.station_name,
                  sessionContext?.exercise_name || planningFocus?.exercise_name,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : null}

            {/* Agent team strip: not for coach/admin — keep Pingflo focused on the active agent */}
            {!hideHeader &&
            !planAssistMode &&
            !isPlayer &&
            homeRole === "monitor" ? (
              <div className="digiping-chat__agent-bar">
                <div className="agent-team-hud" data-tour-id="pingflo-agent-team" title={t("agentSelectorTitle", "SmartTTClub Agent Team")}>
                  {AGENT_IDS.map((aid) => {
                    const a = AGENTS[aid];
                    const isCurrent = activeAgent.id === a.id;
                    return (
                      <button
                        key={aid}
                        type="button"
                        className={`agent-team-chip ${isCurrent ? "agent-team-chip--active" : ""}`}
                        onClick={() => setSelectedAgentId(aid)}
                        title={`${a.name} — ${t(a.badgeKey, a.defaultBadge)}`}
                      >
                        <span
                          className="agent-team-chip__dot"
                          style={{ background: a.primaryColor }}
                        />
                        {a.name}
                      </button>
                    );
                  })}
                </div>
                <div className="digiping-chat__agent-mission" title={t(activeAgent.missionKey, activeAgent.defaultTagline)}>
                  <span className="digiping-chat__agent-mission-dot" style={{ background: activeAgent.primaryColor }} />
                  <span className="digiping-chat__agent-mission-text">{t(activeAgent.missionKey, activeAgent.defaultTagline)}</span>
                </div>
              </div>
            ) : null}

            {showPeopleTabs && profilingMode !== "technical" && profilingMode !== "clubSetup" && profilingMode !== "clubScforh" ? (
              <div className="dp-session-tabs digiping-chat__main-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mainTab === "digiping"}
                  className={`dp-session-tabs__tab${
                    mainTab === "digiping" ? " dp-session-tabs__tab--active" : ""
                  }`}
                  onClick={() => setMainTab("digiping")}
                >
                  {activeAgent?.name || "Pingflo"}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mainTab === "people"}
                  className={`dp-session-tabs__tab${
                    mainTab === "people" ? " dp-session-tabs__tab--active" : ""
                  }`}
                  onClick={() => {
                    if (selectedPlayerId) {
                      setOpenPeerOnce(selectedPlayerId);
                    } else {
                      setOpenPeerOnce(null);
                    }
                    setMainTab("people");
                  }}
                >
                  {peopleTabLabel}
                </button>
              </div>
            ) : null}

            {isTechnicalProfilingIntro(profilingIntro) ? (
              technicalIntroCard
            ) : profilingIntro === "trainingAxes" ? (
              trainingAxesIntroCard
            ) : profilingIntro === "smartGoals" ? (
              smartGoalsIntroCard
            ) : profilingIntro === "clubJoin" ? (
              clubJoinIntroCard
            ) : profilingIntro === "clubSetup" ? (
              clubSetupIntroCard
            ) : profilingIntro === "clubScforh" ? (
              clubScforhIntroCard
            ) : showPeopleTabs && mainTab === "people" ? (
              <div className="digiping-chat__people-pane">
                <DigipingPeopleChat
                  role={homeRole || "player"}
                  userId={myUserId}
                  tsId={sessionContext?.ts_id || null}
                  teId={sessionContext?.te_id || null}
                  grId={sessionContext?.gr_id || null}
                  clubId={inviteClubId}
                  presentPlayers={presentPlayers}
                  sessionCoachUserId={sessionCoachUserId}
                  initialPeerId={openPeerOnce || initialPeoplePeerId}
                  initialSelectedGroupId={peopleInitialGroupId}
                  onInitialPeerConsumed={() => setOpenPeerOnce(null)}
                />
              </div>
            ) : (
              <>
            {/* Player SMART trail only — staff use Joueurs / player DM trail */}
            {profilingMode !== "technical" &&
            isPlayer &&
            !isStaffHomeRole(homeRole) ? (
              <div className="digiping-chat__smart-rail-wrap">
                <DigipingSmartRail
                  status={smartProfile?.status || {}}
                  activeDimension={smartProfile?.activeDimension || "S"}
                  labels={smartProfile?.labels || dimensionLabels}
                  coachPendingDims={smartProfile?.coachPendingDims || []}
                  coachCaption={t("coach")}
                  onSelectDimension={(dim) => {
                    setSmartProfile((prev) =>
                      prev ? { ...prev, activeDimension: dim } : prev
                    );
                    const sum = smartProfile?.summaries?.[dim];
                    const label = (smartProfile?.labels || dimensionLabels)?.[dim];
                    if (sum || label) {
                      handleQuickAction(
                        sum
                          ? `Let's focus on ${label?.letter || dim} · ${label?.label || dim}: ${sum}`
                          : `Tell me about my ${label?.letter || dim} · ${label?.label || dim} path`
                      );
                    }
                  }}
                />
                {smartProfile?.summaries?.[smartProfile?.activeDimension] ? (
                  <p className="digiping-chat__smart-summary">
                    {smartProfile.summaries[smartProfile.activeDimension]}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="digiping-chat__history">
              {messages.map((msg) => (
                <MessageRow
                  key={msg.id || msg.timestamp?.toString?.() || msg.text}
                  message={msg}
                  coachName={coachName}
                  agentId={activeAgent.id}
                  canEditAnswer={
                    profilingMode === "technical" &&
                    !loading &&
                    !streamingMsgId
                  }
                  editAnswerLabel={editAnswerLabel}
                  onEditAnswer={beginEditProfilingAnswer}
                  hideSmartDimension={profilingMode === "technical"}
                  autoevalBusy={loading || Boolean(streamingMsgId)}
                  onAutoevalSubmit={handleAutoevalSubmit}
                  isStreaming={msg.role === "bot" && msg.id === streamingMsgId}
                  onStreamTick={() => {
                    scrollChatHistoryToEnd(messagesEndRef.current, "auto");
                  }}
                  onStreamComplete={() => {
                    setStreamingMsgId((cur) => (cur === msg.id ? null : cur));
                  }}
                />
              ))}

              {loading ? (
                <div className="digiping-chat__typing">
                  <div className="digiping-chat__avatar" title={coachName}>
                    <AgentMark agentId={activeAgent.id} size={28} />
                  </div>
                  <div className="digiping-chat__ai-bubble">
                    <div className="digiping-chat__coach-label-row">
                      <span className="digiping-chat__label">{coachName}</span>
                    </div>
                    <div className="digiping-chat__typing-dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              ) : null}

              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 1 &&
            !loading &&
            !planAssistMode &&
            profilingMode !== "technical" &&
            profilingMode !== "clubSetup" &&
            profilingMode !== "clubScforh" ? (
              <div className="digiping-chat__quick">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="digiping-chat__chip"
                    onClick={() => {
                      if (action.openInviteRoster) {
                        setInviteRosterOpen(true);
                        return;
                      }
                      handleQuickAction(action.textKey ? t(action.textKey, action.text) : action.text);
                    }}
                  >
                    {action.labelKey ? t(action.labelKey, action.label) : action.label}
                  </button>
                ))}
              </div>
            ) : null}

            {technicalProfilingOptions.length > 0 && !loading && !streamingMsgId && !smartProfileHandoff ? (
              <div className="digiping-chat__profiling-tray animate-slide-up">
                {editingProfilingAnswerId ? (
                  <p className="digiping-chat__profiling-editing-hint">{editingHintLabel}</p>
                ) : null}
                <p className="digiping-chat__profiling-tray-title">
                  {t("digipingQuickAnswers")}
                </p>
                <div className="digiping-chat__profiling-options">
                  {technicalProfilingOptions.map((option, index) => {
                    const optionLabel =
                      typeof option === "string" ? option : option?.label || "";
                    const optionKey =
                      typeof option === "string"
                        ? option
                        : option?.value || option?.label || `option_${index}`;
                    return (
                      <button
                        key={optionKey}
                        type="button"
                        className="digiping-chat__profiling-option ripple"
                        onClick={() => handleProfilingOptionClick(option)}
                        disabled={loading || Boolean(streamingMsgId)}
                      >
                        <span className="digiping-chat__profiling-option-title">{optionLabel}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {smartProfileHandoff &&
            !planAssistMode &&
            !loading &&
            !streamingMsgId ? (
              <div className="digiping-chat__profiling-tray animate-slide-up">
                <p className="digiping-chat__profiling-tray-title">
                  {t("digipingNextStep")}
                </p>
                <p className="digiping-chat__handoff-hint">
                  {t("digipingHandoffHint")}
                </p>
                <div className="digiping-chat__profiling-options">
                  <button
                    type="button"
                    className="digiping-chat__profiling-option digiping-chat__handoff-cta ripple"
                    onClick={confirmSmartProfileHandoff}
                  >
                    <span className="digiping-chat__profiling-option-title">
                      {t("digipingViewSmartGoals")}
                    </span>
                  </button>
                </div>
              </div>
            ) : null}

            <footer className="digiping-chat__footer">
              {canInvitePlayers &&
              (profilingMode !== "clubSetup" && profilingMode !== "clubScforh"
                ? true
                : clubSetupInviteUnlocked) ? (
                <DigipingPlayerInviteRoster
                  expanded={inviteRosterOpen}
                  onToggle={() => setInviteRosterOpen((open) => !open)}
                  onSent={handleInvitesSent}
                  clubId={inviteClubId}
                  lng={digipingLang}
                />
              ) : null}
              {composerOpen && isFixcodeCommand(input) ? (
                <p className="digiping-chat__fixcode-hint">
                  {t("fixcodeHint", "This message goes to the fix desk, not the coach.")}
                </p>
              ) : null}
              <div
                className={`digiping-chat__composer${
                  composerOpen ? "" : " digiping-chat__composer--idle"
                }`}
              >
                {composerOpen ? (
                  <>
                    {canStaffChampionship ? (
                      <>
                        <input
                          ref={spielplanFileRef}
                          type="file"
                          accept="application/pdf,.pdf,.txt,.tsv,.csv,text/plain"
                          hidden
                          onChange={handleSpielplanAttach}
                        />
                        <button
                          type="button"
                          className="digiping-chat__attach"
                          disabled={loading || Boolean(streamingMsgId)}
                          onClick={() => spielplanFileRef.current?.click()}
                          aria-label={t("digipingAttachSpielplan", "Attach league calendar")}
                          title={t("digipingAttachSpielplan", "Attach league calendar")}
                        >
                          <AttachFileOutlinedIcon fontSize="small" />
                        </button>
                      </>
                    ) : null}
                    <textarea
                      ref={inputRef}
                      className="digiping-chat__input"
                      rows={1}
                      readOnly={draftLocked}
                      enterKeyHint="send"
                      placeholder={composerPlaceholder}
                      value={input}
                      onPointerDown={(e) => {
                        if (!draftLocked && !e.currentTarget.readOnly) return;
                        voiceLiveRef.current = false;
                        e.currentTarget.readOnly = false;
                        setDraftLocked(false);
                        setVoiceSendBlocked(false);
                      }}
                      onFocus={(e) => {
                        voiceLiveRef.current = false;
                        e.currentTarget.readOnly = false;
                        setDraftLocked(false);
                        setVoiceSendBlocked(false);
                        composerPinCleanupRef.current?.();
                        composerPinCleanupRef.current = pinComposerAboveKeyboard(e.currentTarget);
                      }}
                      onBlur={() => {
                        composerPinCleanupRef.current?.();
                        composerPinCleanupRef.current = null;
                      }}
                      onChange={(e) => {
                        voiceLiveRef.current = false;
                        setVoiceSendBlocked(false);
                        setInput(e.target.value);
                        if (voiceInputMode) setVoiceInputMode(false);
                        resizeComposerTextarea(e.currentTarget);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  </>
                ) : (
                  <button
                    type="button"
                    className="digiping-chat__composer-idle"
                    onClick={() => setComposerOpen(true)}
                  >
                    {composerPlaceholder}
                  </button>
                )}
                {voiceSupported ? (
                  <div key="pingflo-voice" className="digiping-chat__voice">
                    <CoachVoiceButton
                      disabled={loading}
                      languageHint={digipingLang}
                      onPartial={handleVoicePartial}
                      onListeningChange={handleVoiceListeningChange}
                      onTranscript={handleVoiceTranscript}
                      primaryColor={brand?.color_primary || "#0877BC"}
                    />
                  </div>
                ) : null}
                {composerOpen ? (
                  <button
                    type="button"
                    className="digiping-chat__send"
                    onClick={() => handleSend()}
                    disabled={!input.trim() || loading || voiceSendBlocked}
                    aria-label="Send"
                  >
                    <SendIcon fontSize="small" />
                  </button>
                ) : null}
              </div>
            </footer>
              </>
            )}
  </>
  ) : null;

  if (isEmbedded) {
    if (!open) return null;
    return (
      <div className="digiping-chat digiping-chat--embedded">
        <div className="digiping-chat__panel digiping-chat__panel--embedded">
          {panelBody}
        </div>
      </div>
    );
  }

  return (
    <div className={`digiping-chat ${activeAgent.themeClass}`}>
      {!open ? (
        <button
          type="button"
          className="digiping-chat__fab"
          data-tour-id="pingflo-fab"
          onClick={openChat}
          aria-label={coachName}
          title={`${coachName} · ${t(activeAgent.badgeKey, activeAgent.defaultBadge)}`}
        >
          {messages.length === 0 ? <span className="digiping-chat__fab-dot" /> : null}
          <AgentMark agentId={activeAgent.id} size={36} showGlow />
        </button>
      ) : null}

      {open ? (
        <div
          className={`digiping-chat__shell digiping-chat__shell--fullscreen${
            planAssistMode ? " digiping-chat__shell--planning" : ""
          }`}
          role="dialog"
          aria-label={coachName}
          aria-modal="true"
        >
          <div className="digiping-chat__panel">{panelBody}</div>
        </div>
      ) : null}
    </div>
  );
}
